import './HighestCriticalAlarmsList.scss';

import React from 'react';
import { useSelector } from 'react-redux';
import Tooltip from '@mui/material/Tooltip';
import AlertIcon from '@mui/icons-material/ReportProblemSharp';
import { NavLink } from 'react-router-dom';

import DataTable from 'src/component/UI/DataTable';
import DeviceLinkCombo from 'src/component/UI/DeviceLinkCombo';
import LoadingPlaceholder from 'src/component/UI/LoadingPlaceholder';
import { formatAlarmLabel } from 'src/component/AlarmsList';

import { getDevicesInAlarmWithoutNotification, getDevicesInAlarmLoading } from 'src/module/alarm/selector';
import { getCriticalityLabels, getTemperatureUnit } from 'src/module/authentication/selector';
import { celsiusToFahrenheitConvertor, formatDate } from 'src/utils/utils';
import { useTranslation } from 'src/i18n';

const MAX_ROWS = 10;

export default function HighestCriticalAlarmsList () {
  const { t } = useTranslation();

  const devicesInAlarm = useSelector(getDevicesInAlarmWithoutNotification);
  const loading = useSelector(getDevicesInAlarmLoading);
  const criticalityLabels = useSelector(getCriticalityLabels);
  const temperatureUnit = useSelector(getTemperatureUnit);

  const highestCriticality = (criticalityLabels?.size ?? 0) > 0 ? criticalityLabels.size - 1 : 0;
  const criticalityLabel = criticalityLabels?.get(highestCriticality) || `Criticality ${highestCriticality}`;

  // --- Sort functions ---

  const raisedAtSortFunction = (_a: any, _b: any, sort: number) => {
    const aMostRecent = _a.get('alarms').maxBy((alarm: any) => new Date(alarm.get('alarm_raised_at')).getTime());
    const bMostRecent = _b.get('alarms').maxBy((alarm: any) => new Date(alarm.get('alarm_raised_at')).getTime());
    const a = aMostRecent?.get('alarm_raised_at') ?? null;
    const b = bMostRecent?.get('alarm_raised_at') ?? null;
    if (a && b) return (a < b) ? sort : sort * -1;
    if (!a) return sort * -1;
    return sort;
  };

  const deviceTagAscSort = (a: any, b: any) =>
    ((a.get('controller_tag') || a.get('raw_tag')) > (b.get('controller_tag') || b.get('raw_tag'))) ? 1 : -1;

  const deviceTagDescSort = (a: any, b: any) =>
    ((a.get('controller_tag') || a.get('raw_tag')) < (b.get('controller_tag') || b.get('raw_tag'))) ? 1 : -1;

  const formatAlarmCell = (alarm: any) => {
    const noteExpiredAt = alarm.get('note_expired_at');
    const label = formatAlarmLabel(alarm.get('description'), t);
    if (noteExpiredAt) {
      return (
        <Tooltip title={t('maintenance.noteExpired')} className='warning-text'>
          <div className='cell-with-icon'>
            <span className='icon'><AlertIcon width={16} height={16} /></span>
            <span className='label'>{label}</span>
          </div>
        </Tooltip>
      );
    }
    return label;
  };

  // --- Table columns (basic list, no checkboxes) ---

  const columns: any[] = [
    {
      id: 'tag',
      Header: t('maintenance.deviceTag'),
      accessor: (row: any) => row.get('circuit_tag'),
      sortFunction: deviceTagAscSort,
      sortDescFunction: deviceTagDescSort,
      maxWidth: '200px',
      Cell: (row: any) => <DeviceLinkCombo device={row.cell.row.original} />,
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
    },
    {
      id: 'alarms',
      Header: t('maintenance.criticalAlarms'),
      accessor: (row: any) => row.get('alarms'),
      minWidth: '220px',
      Cell: (row: any) => row.cell.value.map((alarm: any, index: number) => (
        <div key={index} className='alarm-list'>{formatAlarmCell(alarm)}</div>
      )),
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
    },
    {
      id: 'temperature',
      Header: t('maintenance.alarmTemp'),
      accessor: (row: any) => row.get('alarms'),
      Cell: (row: any) => row.cell.value.map((alarm: any, index: number) => (
        <div key={index} className='alarm-list'>
          {temperatureUnit === 'F' && alarm.get('actual_temperature')
            ? celsiusToFahrenheitConvertor(alarm.get('actual_temperature'))
            : alarm.get('actual_temperature')}
        </div>
      )),
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
    },
    {
      id: 'current',
      Header: t('maintenance.alarmCurrent'),
      accessor: (row: any) => row.get('alarms'),
      Cell: (row: any) => row.cell.value.map((alarm: any, index: number) => (
        <div key={index} className='alarm-list'>{alarm.get('actual_current')}</div>
      )),
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : ''
    },
    {
      id: 'alarm_raised_at',
      Header: t('maintenance.alarmRaisedAt'),
      accessor: (row: any) => row.get('alarms'),
      sortFunction: (v1: any, v2: any) => raisedAtSortFunction(v1, v2, 1),
      sortDescFunction: (v1: any, v2: any) => raisedAtSortFunction(v1, v2, -1),
      Cell: (row: any) => row.cell.value.map((alarm: any, index: number) => (
        <div key={index} className='alarm-list'>{formatDate(alarm.get('alarm_raised_at'))}</div>
      )),
      ClassName: (row: any) => row.original.get('invalidated') ? 'line-through' : '',
      minWidth: '220px'
    }
  ];

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (devicesInAlarm.size === 0) {
    return (
      <div id='highest-critical-alarms'>
        <p className='no-alarms'>{t('dashboard.highestCriticalAlarmsNone')}</p>
      </div>
    );
  }

  const displayDevices = devicesInAlarm.size > MAX_ROWS
    ? devicesInAlarm.slice(0, MAX_ROWS)
    : devicesInAlarm;

  return (
    <div id='highest-critical-alarms'>
      <div className='hca-toolbar'>
        <span className='hca-subtitle'>
          {t('dashboard.highestCriticalAlarmsSubtitle', { label: criticalityLabel, count: devicesInAlarm.size })}
        </span>
      </div>

      <DataTable
        stickyHeader
        columns={columns}
        data={displayDevices}
        tableId='highestCriticalAlarmsTable'
      />

      {devicesInAlarm.size > MAX_ROWS && (
        <div className='hca-view-all'>
          <NavLink to='/maintenance'>
            {t('dashboard.viewAllAlarms', { count: devicesInAlarm.size })}
          </NavLink>
        </div>
      )}
    </div>
  );
}
